/**
 * CopyDiagButton — iter84.66 · Release Ops "paste-ready diagnostics".
 *
 * One-click copy of a card's current state as a compact plain-text block
 * the operator can paste straight into the agent chat. Companion to the
 * Deployment Readiness card's base64 `?diag=` URL (which predates this
 * and stays as-is); every other Release Ops surface uses this plain-text
 * format so a human can read what they're pasting.
 */
import { ClipboardCopy } from "lucide-react";
import { toast } from "sonner";

const ICON = {
    pass: "✓", ok: "✓", clear: "✓", success: "✓", compliant: "✓",
    warn: "⚠", ack_required: "⚠", investigating: "⚠",
    fail: "✗", blocked: "✗", block_arm: "✗", failure: "✗", "non-compliant": "✗",
    ack: "☑", waived: "☑",
    notice: "ℹ", pending: "ℹ",
};

export function diagLine(status, label, detail) {
    const s = String(status || "?");
    return `${ICON[s] || "•"} [${s.toUpperCase()}] ${label}${detail ? ` — ${detail}` : ""}`;
}

export function buildDiagText({ card, environment, verdict, summary, lines = [] }) {
    const head = [`[TCR diag] ${card}`, environment ? `env: ${environment}` : null, new Date().toISOString()]
        .filter(Boolean).join(" · ");
    const v = `verdict: ${String(verdict || "unknown").toUpperCase()}${summary ? ` (${summary})` : ""}`;
    const body = lines.length ? lines : ["✓ all rows clean"];
    return [head, v, ...body].join("\n");
}

export function CopyDiagButton({ getText, testId, label = "Copy diag" }) {
    const copy = async (e) => {
        e?.stopPropagation();
        const text = typeof getText === "function" ? getText() : getText;
        if (!text) {
            toast.error("Nothing to copy yet — load or run the card first.");
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Diagnostic copied — paste it into the chat.");
        } catch (_err) {
            window.prompt("Copy this diagnostic:", text);
        }
    };
    return (
        <span
            role="button"
            tabIndex={0}
            onClick={copy}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") copy(e); }}
            data-testid={testId}
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
            title="Copy a paste-ready plain-text diagnostic of this card's current state"
        >
            <ClipboardCopy className="w-3 h-3" /> {label}
        </span>
    );
}
